// =============================================================================
// PmoHomeScreen — /pmo (manager+)
// 22 Oct 2026 (mig 446). The clients with the PMO switch on, one card each
// with the numbers that matter before a meeting, Open dashboard / Start
// meeting. Below, the other active clients with a switch to turn them on
// (James: Tier 1 clients only — Ventia, APD; HVAC and Hardline stay off).
// =============================================================================

import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Presentation, Play, ToggleLeft, ToggleRight, CalendarDays, AlertTriangle, BarChart3 } from "lucide-react";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Spinner } from "@/components/ui";
import { usePmoClients, usePmoWrites, clientName, fmtD, type PmoClient } from "./pmoShared";

export default function PmoHomeScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const { data: clients = [], isLoading } = usePmoClients();
  const { setPmoEnabled, startMeeting } = usePmoWrites(undefined);
  const [showOff, setShowOff] = useState(false);
  if (user && !hasRole(user, "manager")) return <Navigate to="/" replace/>;

  const on  = clients.filter(c => c.pmo_enabled);
  const off = clients.filter(c => !c.pmo_enabled);

  const start = (c: PmoClient) => {
    if (c.open_meeting_id) { navigate(`/pmo/${c.client_id}?meeting=${c.open_meeting_id}`); return; }
    startMeeting.mutate({ client_id: c.client_id }, {
      onSuccess: (row: any) => navigate(`/pmo/${c.client_id}?meeting=${row?.meeting_id ?? ""}`),
      onError: (e: any) => window.alert(e?.message ?? "Could not start the meeting"),
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-24">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Presentation size={22} className="text-accent"/> PMO — client governance</h1>
          <div className="text-text-muted text-[13px] mt-1">One clean dashboard per client for the recurring meeting. Nothing here shows cost, margin or another client.</div>
        </div>
        {hasRole(user, "director") && <Link to="/pmo/portfolio"><Button variant="secondary" icon={BarChart3}>Portfolio — rank the clients</Button></Link>}
      </div>

      {isLoading ? <div className="py-16 flex justify-center"><Spinner/></div> : (
        <>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
            {on.map(c => (
              <Card key={c.client_id} className={c.actions_overdue > 0 || c.overdue_defects > 0 ? "border-warn/50" : ""}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[17px] font-semibold">{clientName(c)}</div>
                      <div className="text-[11px] text-text-muted">{[c.carriers, `${c.live_count + c.acceptance_count} active site${c.live_count + c.acceptance_count === 1 ? "" : "s"}`, c.last_meeting_date ? `last meeting ${fmtD(c.last_meeting_date)}` : "no meeting yet"].filter(Boolean).join(" · ")}</div>
                    </div>
                    {c.open_meeting_id && <span className="text-[10px] font-mono uppercase tracking-wider2 text-accent border border-accent/40 rounded px-1.5 py-0.5">meeting open</span>}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    <Num n={c.live_count} label="live"/>
                    <Num n={c.upcoming_count} label="upcoming"/>
                    <Num n={c.open_defects} label="defects open" tone={c.overdue_defects > 0 ? "text-danger" : c.open_defects > 0 ? "text-warn" : undefined} sub={c.overdue_defects > 0 ? `${c.overdue_defects} overdue` : undefined}/>
                    <Num n={c.actions_open} label="actions" tone={c.actions_overdue > 0 ? "text-danger" : undefined} sub={c.actions_overdue > 0 ? `${c.actions_overdue} overdue` : undefined}/>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <Link to={`/pmo/${c.client_id}`}><Button variant="primary" size="sm" icon={Presentation}>Open dashboard</Button></Link>
                    <Button variant="secondary" size="sm" icon={c.open_meeting_id ? CalendarDays : Play} disabled={startMeeting.isPending} onClick={() => start(c)}>{c.open_meeting_id ? "Resume meeting" : "Start meeting"}</Button>
                  </div>
                </div>
              </Card>
            ))}
            {on.length === 0 && (
              <Card><div className="p-6 text-[13px] text-text-muted flex items-start gap-2"><AlertTriangle size={16} className="text-warn mt-0.5"/>No client has the PMO switched on yet. Turn on Ventia and APD below.</div></Card>
            )}
          </div>

          <button type="button" onClick={() => setShowOff(v => !v)} className="text-[12px] text-text-muted hover:text-text inline-flex items-center gap-1.5 mb-2">
            {showOff ? <ToggleRight size={16} className="text-accent"/> : <ToggleLeft size={16}/>} Switch a client on ({off.length} off)
          </button>
          {showOff && (
            <Card>
              <div className="divide-y divide-line">
                {off.map(c => (
                  <div key={c.client_id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-[13px]">
                    <div><span className="font-medium">{clientName(c)}</span> <span className="text-text-muted text-[11px]">· {c.live_count} live · {c.bids_open + c.bids_responded} bids in play</span></div>
                    <Button size="sm" variant="secondary" disabled={setPmoEnabled.isPending} onClick={() => setPmoEnabled.mutate({ client_id: c.client_id, on: true })}>Switch on</Button>
                  </div>
                ))}
                {off.length === 0 && <div className="px-4 py-3 text-[12px] text-text-muted">Every active client is on.</div>}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Num({ n, label, tone, sub }: { n: number; label: string; tone?: string; sub?: string }) {
  return (
    <div>
      <div className={`text-[22px] font-semibold font-mono leading-none ${tone ?? (n === 0 ? "text-text-dim" : "text-text")}`}>{n}</div>
      <div className="text-[10px] text-text-muted mt-1 leading-tight">{label}{sub && <span className={`block ${tone ?? ""}`}>{sub}</span>}</div>
    </div>
  );
}
